import { Card, CardActions, CardContent, Price } from '@faststore/ui'
import { useEffect, useState } from 'react'
import { TrashIcon } from 'src/components/icons/TrashIcon'
import type { AdditionalProperty } from 'src/components/sections/NewPDP/typings'
import { translateFrequencies } from 'src/components/sections/NewPDP/utilities'
// import { DiscountBadge } from 'src/components/ui/Badge'
import Button from 'src/components/ui/Button'
import { Image } from 'src/components/ui/Image'
import QuantitySelector from 'src/components/ui/QuantitySelector'
// import { useProductSpecifications } from 'src/hooks/useProductSpecifications'
import { useRemoveButton } from 'src/sdk/cart/useRemoveButton'
import { useUpdateQuantity } from 'src/sdk/cart/useUpdateQuantity'
import type { CartItem as ICartItem } from 'src/sdk/cart/validate'
import { useFormattedPrice } from 'src/sdk/product/useFormattedPrice'
import { getPriceInfo } from 'src/utils'
import './cart-item.scss'
import CartItemLoyaltyPoints from './CartItemLoyaltyPoints'

interface Props {
  item: ICartItem
  loyaltyMultipliers: string[]
  isCartValidating: boolean
  setIsVTEXValidating: any
  handleSubTotal: (sku: string, originalPrice: number) => void
}

function CartItem({
  item,
  loyaltyMultipliers,
  isCartValidating,
  setIsVTEXValidating,
  handleSubTotal,
}: Props) {
  const isKit = item?.itemOffered?.stockKeepingUnit?.isKit
  const { onClick: onClickRemove, ...btnProps } = useRemoveButton(
    { ...item, isKit },
    setIsVTEXValidating
  )
  const itemSku = item?.itemOffered?.sku

  const itemOffered = item?.itemOffered // The itemOffered property of the item from the cart
  const itemVariantFiltered = itemOffered?.isVariantOf?.hasVariant.filter(
    (i) => i.sku === itemSku
  )

  // Info from the item from the cart to get the price infos
  const addedItemUnitMultiplier =
    itemOffered?.productSpecifications?.unitMultiplier ?? 1
  const minPriceThirtyDays = itemOffered?.structuredData?.minPriceThirtyDays
  const itemPrice = itemVariantFiltered?.[0]?.offers?.lowPrice || 0
  const listPrice = item?.listPrice || 0
  const sku = itemOffered?.sku || ''

  const priceInfo = getPriceInfo({
    listPrice,
    lowPrice: itemPrice,
    unitMultiplier: addedItemUnitMultiplier,
    minPriceThirtyDays,
    skuId: sku,
  })

  const {
    isDiscount: hasDiscount,
    discount: discountPercentage,
    verifiedOriginalPrice,
    verifiedOriginalPriceFlat,
    mustShowDiscountPercentage,
    verifiedPriceToShow,
    verifiedPriceToShowFlat,
  } = priceInfo

  useEffect(() => {
    handleSubTotal(
      item.itemOffered.sku,
      (verifiedOriginalPrice || verifiedPriceToShow) * item.quantity // originalPrice if product hasDiscount otherwise priceToShow
    )
  }, [verifiedOriginalPrice, verifiedPriceToShow, item.quantity])

  const unitMultiplier = item.itemOffered.productSpecifications?.unitMultiplier ?? 1;

  const { updateItemQuantity, error } = useUpdateQuantity(
    {
      ...item,
      isKit,
      price: unitMultiplier > 1 ? (100 * item.price / unitMultiplier) / 100 : item.price
    },
    setIsVTEXValidating
  )

  const { price } = item
  const totalPriceByItem = price * item.quantity // we use price here to avoid the rounding of the price shown in the minicart
  const [hasStock, setHasStock] = useState(Boolean)

  // const productSpecifications = useProductSpecifications(item.itemOffered.sku)

  const outOfStockCard = hasStock ? '' : 'outOfStockCard'

  const subscriptions = item?.itemOffered?.additionalProperty?.filter(
    (el: AdditionalProperty) =>
      el.valueReference === 'ATTACHMENT' &&
      el.name.includes('vtex.subscription')
  )

  const targetMultiplier = loyaltyMultipliers.find((el) => {
    const [sku] = el.split('-')
    return sku === item?.itemOffered?.sku
  })
  const loyaltyMultiplier = Number(targetMultiplier?.split('-')[1]) || 1

  const loyaltyPoints = !hasStock
    ? 0
    : subscriptions?.length > 0
    ? Math.trunc(totalPriceByItem * 0.95) * loyaltyMultiplier
    : Math.trunc(totalPriceByItem) * loyaltyMultiplier

  useEffect(() => {
    setHasStock(itemPrice > 0)
  }, [itemPrice])

  return (
    <Card
      className={`cart-item ${outOfStockCard}`}
      data-testid="cart-item"
      data-sku={item?.itemOffered?.sku}
      data-seller={item?.seller?.identifier}
    >
      <CardContent>
        {error && (
          <div className="error">
            <p>{error}</p>
          </div>
        )}
        <div className="top">
          <div className="itemImage__container" style={{ display: 'block' }}>
            <Image
              baseUrl={item.itemOffered.image?.[0].url}
              alt={item.itemOffered.image?.[0].alternateName}
              sourceWidth={360}
              aspectRatio={1}
              width={110}
              breakpoints={[50, 100, 150]}
              loading="eager"
              layout="constrained"
              backgroundColor="#f0f0f0"
              options={{
                fitIn: true,
              }}
              imgStyle={{ display: 'block' }}
              style={{ display: 'block' }}
            />
          </div>
          <div data-cart-item-summary>
            <div>
              <p className="text__body" data-cart-item-title>
                {item.itemOffered.isVariantOf.name}
              </p>
            </div>
            {targetMultiplier && (
              <div className="item-loyalty-points-wrapper">
                <CartItemLoyaltyPoints loyaltyPoints={loyaltyPoints} />
              </div>
            )}
            <span data-cart-item-prices>
              {/* {!isZeroOrLess && (
                <DiscountBadge
                  listPrice={listPrice}
                  spotPrice={hasStock ? itemPrice : price}
                  big
                />
              )} */}
              <Price
                value={verifiedPriceToShowFlat}
                formatter={useFormattedPrice}
                testId="price"
                data-value={verifiedPriceToShowFlat}
                variant="spot"
                className={`${
                  hasDiscount ? 'text-white bg-red-500 px-1' : ''
                } block text-sm !font-semibold`}
              />
              {hasDiscount && verifiedOriginalPriceFlat && (
                <div className="flex gap-1 text-[10px]">
                  <Price
                    value={verifiedOriginalPriceFlat}
                    formatter={useFormattedPrice}
                    testId="original-price"
                    data-value={verifiedOriginalPriceFlat}
                    variant="listing"
                    className={`${
                      mustShowDiscountPercentage ? 'line-through' : ''
                    } text-gray-700`}
                  />
                  {mustShowDiscountPercentage && discountPercentage && (
                    <span className="text-red-500">
                      (-{discountPercentage}%)
                    </span>
                  )}
                </div>
              )}
            </span>
          </div>
          <Button
            variant="tertiary"
            icon={
              <TrashIcon
                width={28}
                height={26}
                color="#14181F"
                viewBox="0 0 28 26"
              />
            }
            iconPosition="left"
            {...btnProps}
            className="top__icon"
            onClick={isCartValidating ? undefined : onClickRemove}
          />
        </div>

        {!hasStock && (
          <div className="outofStock__message-container">
            <p>
              Questo articolo non è al momento disponibile.
              <br />
              <strong className="strongText-out">
                Rimuovi questo articolo
              </strong>{' '}
              per procedere <br />
              <span>all'acquisto.</span>
            </p>
          </div>
        )}

        <div className="bottom">
          <CardActions>
            <QuantitySelector
              min={1}
              initial={item.quantity}
              // unitMultiplier={productSpecifications.unitMultiplier}
              unitMultiplier={
                item.itemOffered?.productSpecifications?.unitMultiplier
              }
              onChange={(quantity) => {
                updateItemQuantity(quantity)
              }}
              disabled={isCartValidating}
            />
          </CardActions>
          <Price
            value={totalPriceByItem}
            formatter={useFormattedPrice}
            testId="total-price"
            data-value={totalPriceByItem}
            variant="spot"
            className={`${
              hasDiscount ? 'text-white bg-red-500 px-1' : ''
            } block text-lg !font-semibold leading-6`}
          />
        </div>

        {subscriptions?.length > 0 && (
          <div className="subscription-tag">
            <span className="arca-ico-autoship subscription-icon-minicart">
              <span className="path1"></span>
              <span className="path2"></span>
              <span className="path3"></span>
              <span className="path4"></span>
            </span>
            <span className="subscription-tag-title">Ordine Periodico</span>
            <span className="subscription-tag-frequency">Frequenza:</span>
            <span className="subscription-tag-frequency-value">
              {translateFrequencies(
                subscriptions[0]?.value['vtex.subscription.key.frequency']
              )}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default CartItem
